import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import { ShieldCheck, Award, Percent, Clock, UserCheck } from "lucide-react";
import Header from "../components/Header";
import HeroVertical from "../components/HeroVertical";
import ReassuranceBar from "../components/ReassuranceBar";
import VerticalSections from "../components/VerticalSections";
import LeadFormClim from "../components/LeadFormClim";
import FAQVertical from "../components/FAQVertical";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import MobileStickyBar from "../components/MobileStickyBar";
import ConfirmationScreen from "../components/ConfirmationScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASSURANCE_ITEMS = [
  { icon: ShieldCheck, text: "Entreprise certifiée", description: "Wallonie" },
  { icon: Award, text: "Garantie 5 ans", description: "Sur l'installation" },
  { icon: Percent, text: "TVA 6%", description: "Clim réversible éligible" },
  { icon: Clock, text: "Devis sous 24h", description: "Réponse rapide" },
  { icon: UserCheck, text: "Installateur direct", description: "Pas un comparateur" },
];

const WHY_ITEMS = [
  "Un seul interlocuteur, du devis à la pose.",
  "C'est l'artisan qui pose votre clim qui établit votre devis — pas un centre d'appel.",
  "Pas 3 démarcheurs qui vous rappellent. Un pro, point.",
  "Matériel de marque, installation soignée, SAV assuré par nos équipes.",
];

const BENEFITS = [
  "Chauffage en hiver + climatisation en été, en une seule installation.",
  "De vraies économies sur le chauffage, surtout si vous chauffez actuellement à l'électrique.",
  "Éligible à la TVA réduite 6% (parce que réversible).",
  "Silencieuse et pilotable depuis votre smartphone.",
];

const STEPS = [
  "Vous demandez votre devis (2 minutes).",
  "On vous rappelle rapidement et on fixe un rendez-vous.",
  "Installation par nos équipes — garantie 5 ans.",
];

const FAQ_ITEMS = [
  {
    question: "Suis-je éligible à la TVA 6% ?",
    answer:
      "La TVA réduite à 6% s'applique aux logements de plus de 10 ans, à usage privé. Comme notre climatisation est réversible (elle chauffe aussi), elle est éligible à ce taux réduit. Nous vérifions votre éligibilité lors du devis.",
  },
  {
    question: "Quels sont vos délais d'installation ?",
    answer:
      "Après validation du devis, l'installation est généralement planifiée sous 2 à 4 semaines. L'installation elle-même se fait en une journée pour une configuration standard.",
  },
  {
    question: "Quelles zones couvrez-vous ?",
    answer:
      "Nous intervenons dans le Hainaut, le Brabant wallon et dans toute la Wallonie.",
  },
  {
    question: "Le prix « à partir de 1599€ », ça comprend quoi ?",
    answer:
      "Il s'agit d'un prix « à partir de » pour une configuration mono-split réellement disponible à ce tarif, pose et TVA 6% incluses. Le devis final dépend de la surface, du nombre de pièces et de la configuration de votre logement.",
  },
];

const WHATSAPP_MESSAGE =
  "Bonjour, je souhaite un devis pour une climatisation réversible. Pouvez-vous me recontacter ?";

const ClimatisationPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const handleFormSubmit = async (formData) => {
    try {
      await axios.post(`${API}/leads`, formData);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "lead_form_submit",
          form_type: "devis",
          vertical: "climatisation",
          project_type: formData.type_projet,
        });
      }

      setSubmittedName(formData.prenom);
      setFormSubmitted(true);

      if (window.dataLayer) {
        window.dataLayer.push({ event: "lead_confirmation_view", vertical: "climatisation" });
      }

      toast.success("Demande envoyée avec succès !");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      throw error;
    }
  };

  if (formSubmitted) {
    return (
      <>
        <Helmet>
          <title>Demande envoyée | LLDLS Smart Energy</title>
        </Helmet>
        <ConfirmationScreen prenom={submittedName} onBack={() => setFormSubmitted(false)} />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Climatisation Réversible en Wallonie dès 1599€ — Installateur Direct | LLDLS</title>
        <meta
          name="description"
          content="Installation de climatisation réversible par LLDLS, installateur certifié en Wallonie. À partir de 1599€, TVA 6%. Devis gratuit sous 24h. Pas un comparateur."
        />
        <meta property="og:title" content="Climatisation Réversible en Wallonie dès 1599€ — Installateur Direct | LLDLS" />
        <meta
          property="og:description"
          content="Installation de climatisation réversible par LLDLS, installateur certifié en Wallonie. À partir de 1599€, TVA 6%. Devis gratuit sous 24h."
        />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/vssoxae4_IMG_2625.jpeg" />
        <meta property="og:url" content="https://lldls-devis.com/climatisation" />
      </Helmet>
      <div className="min-h-screen bg-slate-50">
        <Header />
        <HeroVertical
          badge="Installateur certifié en Wallonie"
          title={
            <>
              Climatisation réversible <span className="text-amber-500">dès 1599€</span>, installée par LLDLS
            </>
          }
          subtitle={
            <>
              Chauffez l'hiver et rafraîchissez l'été avec une pompe à chaleur air-air réversible.{" "}
              <span className="text-white font-medium">TVA 6% incluse.</span>
            </>
          }
          note="Installateur direct certifié en Wallonie — pas un comparateur."
          ctaLabel="Recevoir mon devis gratuit"
          bgImage="https://static.prod-images.emergentagent.com/jobs/a8c18dea-4113-4c04-9a57-7dea48cd98c1/images/424d685c442c32a0ab56e9ea11766f677cc7e9e60dc0ccaf345fcff000f69b8b.png"
          whatsappMessage={WHATSAPP_MESSAGE}
        />
        <ReassuranceBar items={REASSURANCE_ITEMS} />
        <VerticalSections
          whyItems={WHY_ITEMS}
          benefitsTitle="La clim réversible, c'est :"
          benefits={BENEFITS}
          steps={STEPS}
        />
        <LeadFormClim onSubmit={handleFormSubmit} />
        <FAQVertical items={FAQ_ITEMS} />
        <Footer />
        <WhatsAppButton />
        <MobileStickyBar ctaLabel="Devis" showWhatsApp whatsappMessage={WHATSAPP_MESSAGE} />
      </div>
    </>
  );
};

export default ClimatisationPage;
